import { useEffect, useState } from 'react';
import {
  dashboardService,
  type ResearchChangesQuery,
  type ResearchChangesResponse,
  type ResearchHistoryResponse,
} from '../../../services/dashboard.service';

const messageOf = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

/** An answer, tagged with the request it answers. */
interface Answer<T> {
  key: string;
  data: T | null;
  error: string | null;
}

/**
 * Fetches ``load(key)`` whenever ``key`` changes (nothing while it is null).
 * Only the answer for the current key is returned, so a slow answer for
 * earlier filters or another profile never shows.
 */
function useKeyedFetch<T>(key: string | null, load: (key: string) => Promise<T>, failMsg: string) {
  const [answer, setAnswer] = useState<Answer<T> | null>(null);

  useEffect(() => {
    if (key === null) return;
    let cancelled = false;
    load(key)
      .then((data) => {
        if (!cancelled) setAnswer({ key, data, error: null });
      })
      .catch((err) => {
        if (!cancelled) setAnswer({ key, data: null, error: messageOf(err, failMsg) });
      });
    return () => {
      cancelled = true;
    };
    // `load` and `failMsg` are fixed per caller; the key decides what to fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const current = key !== null && answer?.key === key ? answer : null;
  return { data: current?.data ?? null, error: current?.error ?? null, loading: key !== null && !current };
}

const loadChanges = (key: string) =>
  dashboardService.getResearchChanges(JSON.parse(key) as ResearchChangesQuery);

/** The directory's "Research changes" rows for the given filters. */
export const useResearchChanges = (query: ResearchChangesQuery) => {
  const result = useKeyedFetch<ResearchChangesResponse>(
    JSON.stringify(query), loadChanges, 'Could not load research changes.',
  );
  // Keep the last rows on screen while the next filters load.
  const [shown, setShown] = useState<ResearchChangesResponse | null>(null);
  if (result.data && result.data !== shown) setShown(result.data);
  return { ...result, data: result.data ?? shown };
};

/** Every recorded version of one profile's AI research; idle while null. */
export const useResearchHistory = (alumniId: string | null) =>
  useKeyedFetch<ResearchHistoryResponse>(
    alumniId, dashboardService.getResearchHistory, 'Could not load the research history.',
  );
